import { Fragment, useEffect, useMemo, useState } from "react";

import type { ParamType } from "@uirouter/core";

import type { MonitorAndProtectModelCampaignsCampaignListItemModel } from "@similarweb/pro-api-client";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, Combobox, IconButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { getFaviconUrl } from "app/services/sitesService/sitesUtils";
import { useTranslation } from "pro-features/components/WithTranslation";

import { mnpNavigation } from "../../../app/navigation";
import { useMonitorAndProtectCampaigns } from "../../../services/api/campaign";
import { CampaignPermissions } from "../../../services/asset-permissions";
import { useMonitorAndProtectDataAndParams } from "../../hooks/navigations";
import { Favicon } from "../../ui/layout/favicon";

import type { ICampaignSelectorData } from "./styled";
import { CampaignEmptyState, CampaignSelectedState, ComboboxStyled, MagicBackground } from "./styled";

interface ICampaignOptionProps {
    campaign: MonitorAndProtectModelCampaignsCampaignListItemModel;
    selectedCampaignId?: string | ParamType;
    onCampaignChange: (campaign: MonitorAndProtectModelCampaignsCampaignListItemModel) => void;
}

const CampaignOption = ({ campaign, selectedCampaignId, onCampaignChange }: ICampaignOptionProps) => {
    const translate = useTranslation();

    return (
        <Combobox.Option
            key={campaign.id}
            value={campaign}
            isSelected={selectedCampaignId === campaign.id}
            onClick={() => onCampaignChange(campaign)}
        >
            <Flex
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <Flex
                    alignItems="center"
                    gap="8px"
                >
                    <Favicon
                        iconUrl={getFaviconUrl(campaign.domain)}
                        alt={campaign.domain}
                        size={32}
                    />
                    <Combobox.OptionLabel color={colorsPalettes.carbon[500]}>
                        {campaign.name || translate("monitor_and_protect.shared.campaign_selector.unnamed_campaign")}
                    </Combobox.OptionLabel>
                </Flex>

                {campaign.type === "Demo" ? (
                    <Badge type="demo">{translate("monitor_and_protect.shared.badges.demo")}</Badge>
                ) : null}
            </Flex>
        </Combobox.Option>
    );
};

const fromListCampaign = (campaign: MonitorAndProtectModelCampaignsCampaignListItemModel): ICampaignSelectorData => ({
    id: campaign.id,
    name: campaign.name,
    domain: campaign.domain,
    keywordsCount: campaign.keywordsCount || 0,
});

export const SelectCampaignDropdown = () => {
    const translate = useTranslation();
    const { data: campaignList = [], isFetching } = useMonitorAndProtectCampaigns();
    const { campaignId, ...rest } = useMonitorAndProtectDataAndParams();
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const selectedCampaign = useMemo(() => {
        return campaignId ? campaignList.find((campaign) => campaign.id === campaignId) : null;
    }, [campaignId, campaignList]);

    const filteredCampaigns = useMemo(() => {
        return campaignList.filter((campaign) => {
            const lowerCaseSearchValue = searchValue.toLowerCase();
            return campaign.name.toLowerCase().includes(lowerCaseSearchValue);
        });
    }, [campaignList, searchValue]);

    const categorizedCampaign = useMemo(() => {
        return filteredCampaigns.reduce(
            (acc, campaign) => {
                const currentCampaignPermissions = new CampaignPermissions(campaign);
                if (campaign.type === "Demo") {
                    acc.demo.push(campaign);
                } else if (currentCampaignPermissions.isOwner) {
                    acc.my.push(campaign);
                } else {
                    acc.shared.push(campaign);
                }
                return acc;
            },
            { my: [], shared: [], demo: [] } as Record<
                "my" | "shared" | "demo",
                MonitorAndProtectModelCampaignsCampaignListItemModel[]
            >,
        );
    }, [filteredCampaigns]);

    // Auto-open only once the campaign list has settled. While fetching, a freshly
    // created campaignId is not yet in the (stale) list, so selectedCampaign is
    // transiently null — auto-opening here spuriously shows the dialog and traps it open.
    const shouldAutoOpenCampaignSelect = !isFetching && !selectedCampaign && !isOpen;

    useEffect(() => {
        if (shouldAutoOpenCampaignSelect) {
            setIsOpen(true);
        }
    }, [shouldAutoOpenCampaignSelect]);

    const handleCampaignChange = (campaign: MonitorAndProtectModelCampaignsCampaignListItemModel) => {
        const params = { ...rest };

        for (const key in params) {
            params[key] = null;
        }
        mnpNavigation.updateQueryParams({
            campaignId: campaign.id,
            ...params,
        });
    };

    const handleRedirectToSettings = () => {
        void mnpNavigation.settings.go({
            campaignId,
        });
    };

    return (
        <Fragment>
            <MagicBackground $active={isOpen} />
            <Flex
                alignItems="center"
                gap="8px"
            >
                <ComboboxStyled
                    isOpen={isOpen}
                    onOpenChange={setIsOpen}
                    placement="top-start"
                    offset={-40}
                >
                    <Combobox.Trigger>
                        <div>
                            {selectedCampaign ? (
                                <CampaignSelectedState campaign={fromListCampaign(selectedCampaign)} />
                            ) : (
                                <CampaignEmptyState />
                            )}
                        </div>
                    </Combobox.Trigger>

                    <Combobox.SearchInput
                        value={searchValue}
                        onChange={setSearchValue}
                        placeholder={translate("monitor_and_protect.component.campaign_select.placeholder")}
                    />

                    <Combobox.Content emptyState={translate("monitor_and_protect.component.campaign_select.no_data")}>
                        {categorizedCampaign.my.length > 0 ? (
                            <Combobox.Section>
                                <Combobox.SectionHeader>
                                    {translate("monitor_and_protect.component.campaign_select.header")}
                                </Combobox.SectionHeader>
                                {categorizedCampaign.my.map((campaign) => (
                                    <CampaignOption
                                        key={campaign.id}
                                        campaign={campaign}
                                        selectedCampaignId={campaignId}
                                        onCampaignChange={handleCampaignChange}
                                    />
                                ))}
                            </Combobox.Section>
                        ) : null}
                        {categorizedCampaign.shared.length > 0 ? (
                            <Combobox.Section>
                                <Combobox.SectionHeader>
                                    {translate("monitor_and_protect.component.campaign_select.shared.header")}
                                </Combobox.SectionHeader>
                                {categorizedCampaign.shared.map((campaign) => (
                                    <CampaignOption
                                        key={campaign.id}
                                        campaign={campaign}
                                        selectedCampaignId={campaignId}
                                        onCampaignChange={handleCampaignChange}
                                    />
                                ))}
                            </Combobox.Section>
                        ) : null}
                        {categorizedCampaign.demo.length > 0 ? (
                            <Combobox.Section>
                                <Combobox.SectionHeader>
                                    {translate("monitor_and_protect.shared.campaign_selector.demo_campaigns")}
                                </Combobox.SectionHeader>
                                {categorizedCampaign.demo.map((campaign) => (
                                    <CampaignOption
                                        key={campaign.id}
                                        campaign={campaign}
                                        selectedCampaignId={campaignId}
                                        onCampaignChange={handleCampaignChange}
                                    />
                                ))}
                            </Combobox.Section>
                        ) : null}
                    </Combobox.Content>
                </ComboboxStyled>
                <IconButton
                    type="flat"
                    iconName="settings"
                    iconSize="sm"
                    onClick={handleRedirectToSettings}
                />
            </Flex>
        </Fragment>
    );
};
